import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  labelOf,
  PAYMENT_METHOD_LABELS,
  PAYMENT_PROVIDER_LABELS,
} from '@/lib/enumLabels';
import { Button, PageShell, SectionCard, shortenId } from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import type {
  AdminPaymentDetail,
  AdminRole,
  AdminVoidRequest,
} from '@/lib/api-admin/types';
import { useAuthContext } from '@/providers/AuthProvider';
import { useOrderQuery } from '@/features/orders/hooks/useOrderQuery';
import { PaymentStatusPill } from '../components/PaymentStatusPill';
import { RefundActionModal, type RefundActionBody } from '../components/RefundActionModal';
import { refundScopeItemsOf } from '../lib/refundScope';
import { VoidActionModal } from '../components/VoidActionModal';
import { WebhookEventLog } from '../components/WebhookEventLog';
import { RefundsList } from '../components/RefundsList';
import {
  usePaymentQuery,
  usePaymentWebhooksQuery,
} from '../hooks/usePaymentQuery';
import {
  useManualRefundMutation,
  useRefundMutation,
  useVoidMutation,
  type ManualRefundRequestBody,
} from '../hooks/usePaymentActionMutation';
import { ManualRefundModal } from '../components/ManualRefundModal';
import { LegacyPgInfoCard } from '../components/LegacyPgInfoCard';
import { CASH_PAYMENT_METHODS, refundFlagsOf, TOSS_CASH_PAYMENT_METHODS } from '../lib/refundFlags';
import { minorToMajorString } from '@/features/orders/lib/chargeMoney';
import { canPerformPaymentAction } from '../lib/paymentActionRbac';
import { claimRefundAccountPrefill } from '../lib/claimRefundAccount';
import { useClaimQuery } from '@/features/claims/hooks/useClaimQuery';
import { formatPaymentMinorAmount } from '@/features/orders/lib/chargeMoney';

type ActiveModal = 'refund' | 'manualRefund' | 'void' | null;

// [pay-currency] AdminPaymentDetail additive charge* — be-snapshot 계약(major decimal STRING,
// 미지원 통화는 키 생략 → minor→major 변환 폴백). generated 타입 미반영이라 로컬 확장.
type AdminPaymentDetailExtended = AdminPaymentDetail & {
  chargeAmount?: string | null;
  chargeRefundedAmount?: string | null;
  chargeRemainingRefundableAmount?: string | null;
};

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const isVoidable = (p: AdminPaymentDetail): boolean =>
  p.paymentStatus === 'AUTHORIZED';

const isRefundable = (p: AdminPaymentDetail): boolean =>
  (p.remainingRefundableAmount ?? 0) > 0 &&
  (p.paymentStatus === 'CAPTURED' || p.paymentStatus === 'PARTIALLY_REFUNDED');

export const PaymentDetailPage = () => {
  const { t } = useTranslation('payments');
  const { paymentId } = useParams<{ paymentId: string }>();
  const query = usePaymentQuery(paymentId);
  const webhookQuery = usePaymentWebhooksQuery(paymentId);
  const { currentAdmin } = useAuthContext();
  const roles = useMemo<ReadonlyArray<AdminRole>>(
    () => currentAdmin?.roles ?? [],
    [currentAdmin],
  );

  const refundMutation = useRefundMutation();
  const manualRefundMutation = useManualRefundMutation();
  const voidMutation = useVoidMutation();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  // 클레임에서 진입(?claimId=…)하면 고객이 클레임에 남긴 환불 계좌로 환불 모달을 미리 채운다.
  const [searchParams] = useSearchParams();
  const claimQuery = useClaimQuery(searchParams.get('claimId') ?? undefined);
  const claimAccount = useMemo(() => claimRefundAccountPrefill(claimQuery.data), [claimQuery.data]);
  // QA-311: 결제 상세 응답은 orderNo 를 채우지 않는다(payment toDetail 이 null — 목록만 보강). 주문을 조회해 주문번호를
  // 보인다. 주문 조회 권한이 없으면(403) 종전처럼 "-".
  // QA-625: 환불 구분(상품 환불)의 상품 목록도 이 주문 상세 items[] 에서 쓴다(환불 가능 수량·청구통화 단가).
  const orderForNo = useOrderQuery(query.data?.orderId ?? undefined);
  const scopeItems = useMemo(() => refundScopeItemsOf(orderForNo.data), [orderForNo.data]);

  if (query.isPending) {
    return (
      <p className="py-8 text-center text-sm text-[color:var(--content-muted)]">
        {t('payments.detail.loading')}
      </p>
    );
  }
  if (query.isError || !query.data) {
    return (
      <p
        className="py-8 text-center text-sm"
        role="alert"
        style={{ color: 'var(--status-danger-fg)' }}
      >
        {t('payments.detail.error')}
      </p>
    );
  }

  const payment = query.data;
  const orderNo = payment.orderNo ?? orderForNo.data?.orderNo ?? null;
  const webhooks = webhookQuery.data ?? [];

  const handleRefundSubmit = (body: RefundActionBody) => {
    if (!paymentId) return;
    refundMutation.mutate(
      { paymentId, body },
      { onSuccess: () => setActiveModal(null) },
    );
  };

  const handleManualRefundSubmit = (body: ManualRefundRequestBody, idempotencyKey: string) => {
    if (!paymentId) return;
    manualRefundMutation.mutate({ paymentId, body, idempotencyKey }, { onSuccess: () => setActiveModal(null) });
  };

  const handleVoidSubmit = (body: AdminVoidRequest) => {
    if (!paymentId) return;
    voidMutation.mutate(
      { paymentId, body },
      { onSuccess: () => setActiveModal(null) },
    );
  };

  const errorMessageFor = (err: Error | null): string | null =>
    err ? friendlyMessage(err, t('payments.actionModal.unknownError')) : null;

  const refundErr = errorMessageFor(refundMutation.error ?? null);
  const manualRefundErr = errorMessageFor(manualRefundMutation.error ?? null);
  // 버튼 조합: 수기 전용 → [수기 환불 기록]만 / Toss 가상계좌 → [환불(Toss 송금)] + [수기 환불 기록(계좌이체)] / 그 외 → [환불].
  const flags = refundFlagsOf(payment);
  const { manualOnly, isLegacy, legacyPg, pgCancelled } = flags;
  const method = String(payment.paymentMethod ?? '');
  // QA-438 — 현금성 결제는 카드 승인 취소 대상이 아니라 버튼을 숨기고, 토스 현금성(계좌이체·가상계좌)은 토스 관리자에서
  //   수기 환불한 뒤 상태만 남기도록 수기 환불 기록을 연다(방법: 토스 관리자 환불 / 계좌이체 / 기타).
  const isCashPayment = CASH_PAYMENT_METHODS.has(method);
  const isTossCash = !manualOnly && TOSS_CASH_PAYMENT_METHODS.has(method);
  const manualAvailable = flags.manualAvailable || isTossCash;
  const claimForPayment = claimQuery.data?.orderId === payment.orderId ? claimQuery.data : undefined;
  // 클레임에서 진입했으면 클레임의 예상 환불액(minor)을 수기 환불 기본 금액(major)으로.
  const claimExpected = (claimForPayment as { expectedRefundAmount?: { amount: number; currencyCode: string } | null } | undefined)
    ?.expectedRefundAmount;
  const claimDefaultAmountStr =
    claimExpected && claimExpected.currencyCode === payment.currencyCode
      ? minorToMajorString(claimExpected.amount, claimExpected.currencyCode)
      : null;
  const canRefund = canPerformPaymentAction('refund', roles) && isRefundable(payment);
  const claimAccountForPayment = claimQuery.data?.orderId === payment.orderId ? claimAccount : null;
  const voidErr = errorMessageFor(voidMutation.error ?? null);

  return (
    <PageShell
      title={
        <span className="flex flex-wrap items-center gap-2">
          {t('payments.detail.title', {
            orderNo: orderNo ?? payment.paymentId,
          })}
          <PaymentStatusPill
            displayStatus={payment.displayStatus}
            paymentId={payment.paymentId}
          />
          {isLegacy ? (
            <span
              className="rounded-full px-2 py-0.5 text-xs font-medium"
              style={{ background: 'var(--status-neutral-bg)', color: 'var(--status-neutral-fg)' }}
              data-testid="payment-legacy-badge"
            >
              {t('payments.detail.legacyBadge', '고도몰 이관')}
            </span>
          ) : null}
        </span>
      }
      breadcrumb={[
        { label: t('payments.list.title'), href: '/payments' },
        { label: orderNo ?? payment.paymentId },
      ]}
    >

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <SectionCard className="space-y-4 self-start lg:sticky lg:top-4">
          <div className="mb-2 grid grid-cols-[110px_1fr] items-baseline gap-2 text-sm">
            <span className="text-[color:var(--content-muted)]">
              {t('payments.detail.paymentId')}
            </span>
            <span
              className="text-[color:var(--content-strong)] font-mono"
              title={payment.paymentId}
            >
              {shortenId(payment.paymentId, { prefix: 8, suffix: 4 })}
            </span>
          </div>
          <InfoBlock label={t('payments.detail.orderNo')} value={orderNo ?? '-'} />
          {payment.orderId ? (
            <div className="mb-2 grid grid-cols-[110px_1fr] items-baseline gap-2 text-sm">
              <span className="text-[color:var(--content-muted)]">
                {t('payments.detail.orderLink')}
              </span>
              <Link
                to={`/orders/${payment.orderId}`}
                className="text-[color:var(--accent-emphasis)] underline-offset-2 hover:underline"
              >
                {orderNo ?? payment.orderId}
              </Link>
            </div>
          ) : null}
          <InfoBlock
            label={t('payments.detail.paymentMethod')}
            value={labelOf(PAYMENT_METHOD_LABELS, payment.paymentMethod)}
          />
          <InfoBlock
            label={t('payments.detail.provider')}
            value={labelOf(PAYMENT_PROVIDER_LABELS, payment.provider)}
          />
          {/* [pay-currency] amount/refunded/refundable 은 minor 정수(USD 센트) — 표기는 charge*
              (major decimal STRING) 정본, 미제공 시 minor→major 정확 변환 폴백. KRW 는 ₩ 표기. */}
          <InfoBlock
            label={t('payments.detail.amount')}
            value={formatPaymentMinorAmount(
              payment.amount,
              payment.currencyCode,
              (payment as AdminPaymentDetailExtended).chargeAmount,
            )}
          />
          <InfoBlock
            label={t('payments.detail.refundedAmount')}
            value={formatPaymentMinorAmount(
              payment.refundedAmount ?? 0,
              payment.currencyCode,
              (payment as AdminPaymentDetailExtended).chargeRefundedAmount,
            )}
          />
          <InfoBlock
            label={t('payments.detail.refundable')}
            value={formatPaymentMinorAmount(
              payment.remainingRefundableAmount ?? 0,
              payment.currencyCode,
              (payment as AdminPaymentDetailExtended).chargeRemainingRefundableAmount,
            )}
          />
          <InfoBlock
            label={t('payments.detail.createdAt')}
            value={formatDateTime(payment.createdAt)}
          />
          <InfoBlock
            label={t('payments.detail.capturedAt')}
            value={formatDateTime(payment.capturedAt)}
          />

          {payment.cardInfo ? (
            <div
              className="border-t pt-3"
              style={{ borderColor: 'var(--surface-1)' }}
              data-sentry-mask
              data-testid="payment-card-info"
            >
              <h3 className="mb-2 text-sm font-semibold text-[color:var(--content-strong)]">
                {t('payments.detail.cardInfo')}
              </h3>
              <InfoBlock
                label={t('payments.detail.cardBrand')}
                value={payment.cardInfo.brand}
              />
              <InfoBlock
                label={t('payments.detail.cardLast4')}
                value={`**** **** **** ${payment.cardInfo.last4}`}
              />
              {payment.cardInfo.bin ? (
                <InfoBlock
                  label={t('payments.detail.cardBin')}
                  value={`${payment.cardInfo.bin}******`}
                />
              ) : null}
              {payment.cardInfo.issuerName ? (
                <InfoBlock
                  label={t('payments.detail.cardIssuer')}
                  value={payment.cardInfo.issuerName}
                />
              ) : null}
            </div>
          ) : null}

          {payment.virtualAccount ? (
            <div
              className="border-t pt-3"
              style={{ borderColor: 'var(--surface-1)' }}
            >
              <h3 className="mb-2 text-sm font-semibold text-[color:var(--content-strong)]">
                {t('payments.detail.virtualAccount')}
              </h3>
              <InfoBlock
                label={t('payments.detail.vaBank')}
                value={payment.virtualAccount.bankName ?? payment.virtualAccount.bankCode}
              />
              <InfoBlock
                label={t('payments.detail.vaAccountNumber')}
                value={payment.virtualAccount.accountNumber}
              />
              <InfoBlock
                label={t('payments.detail.vaDueAt')}
                value={formatDateTime(payment.virtualAccount.dueAt)}
              />
            </div>
          ) : null}
        </SectionCard>

        <div className="space-y-6">
          {legacyPg ? <LegacyPgInfoCard legacyPg={legacyPg} /> : null}
          <SectionCard title={t('payments.detail.refunds')}>
            <RefundsList
              currencyCode={payment.currencyCode}
              refunds={payment.refunds}
            />
          </SectionCard>

          <SectionCard title={t('payments.detail.webhooks')}>
            {webhookQuery.isPending ? (
              <p className="text-sm text-[color:var(--content-muted)]">
                {t('payments.detail.webhooksLoading')}
              </p>
            ) : webhookQuery.isError ? (
              <p
                className="text-sm"
                role="alert"
                style={{ color: 'var(--status-danger-fg)' }}
              >
                {t('payments.detail.webhooksError')}
              </p>
            ) : (
              <WebhookEventLog events={webhooks} />
            )}
          </SectionCard>
        </div>
      </div>

      <footer
        className="flex flex-wrap items-center justify-end gap-2 rounded-xl p-4"
        style={{ background: 'var(--surface-raised)' }}
        aria-label={t('payments.detail.actionsLabel')}
      >
        {/* 이관·수기주문(PG 결제키 없음)도 PG 승인 취소가 불가능하다 — 숨긴다(FE 요청: 고도몰 이관 현금성 포함). */}
        {!isCashPayment && !manualOnly ? (
          <Button
            type="button"
            variant="outline"
            size="md"
            disabled={!canPerformPaymentAction('void', roles) || !isVoidable(payment)}
            onClick={() => setActiveModal('void')}
            data-action="void"
          >
            {t('payments.actions.void')}
          </Button>
        ) : null}
        {!manualOnly ? (
          <Button
            type="button"
            variant="danger"
            size="md"
            disabled={!canRefund}
            onClick={() => setActiveModal('refund')}
            data-action="refund"
          >
            {manualAvailable ? t('payments.actions.refundToss', '환불(Toss 송금)') : t('payments.actions.refund')}
          </Button>
        ) : null}
        {manualAvailable ? (
          <Button
            type="button"
            variant={manualOnly ? 'danger' : 'outline'}
            size="md"
            // 고도몰에서 이미 PG 취소된 결제는 기록하면 중복 환불이다.
            disabled={!canRefund || pgCancelled}
            onClick={() => setActiveModal('manualRefund')}
            data-action="manual-refund"
          >
            {manualOnly
              ? t('payments.actions.manualRefund', '수기 환불 기록')
              : isTossCash
                ? t('payments.actions.manualRefundTossCash', '환불 상태 변경(토스 수기 환불)')
                : t('payments.actions.manualRefundTransfer', '수기 환불 기록(계좌이체)')}
          </Button>
        ) : null}
      </footer>

      <RefundActionModal
        payment={payment}
        isOpen={activeModal === 'refund'}
        isSubmitting={refundMutation.isPending}
        errorMessage={refundErr}
        onSubmit={handleRefundSubmit}
        onCancel={() => setActiveModal(null)}
        // 다른 주문의 클레임 id 가 붙어 들어와도 그 계좌를 쓰지 않는다.
        prefillRefundAccount={claimAccountForPayment}
        claimId={claimForPayment?.claimId ?? null}
        scopeItems={scopeItems}
      />

      {manualAvailable ? (
        <ManualRefundModal
          payment={payment}
          isOpen={activeModal === 'manualRefund'}
          isSubmitting={manualRefundMutation.isPending}
          errorMessage={manualRefundErr}
          // 토스 현금성도 방법을 고른다(기본: 원 PG = 토스 관리자에서 환불).
          manualRefundOnly={manualOnly || isTossCash}
          {...(isTossCash && !manualOnly ? { allowedMethods: ['ORIGINAL_PG', 'BANK_TRANSFER'] as const } : {})}
          legacyPg={legacyPg}
          prefillRefundAccount={claimAccountForPayment}
          claimId={claimForPayment?.claimId ?? null}
          defaultAmount={claimDefaultAmountStr != null ? Number(claimDefaultAmountStr) : null}
          scopeItems={scopeItems}
          onSubmit={handleManualRefundSubmit}
          onCancel={() => setActiveModal(null)}
        />
      ) : null}

      <VoidActionModal
        paymentNo={orderNo ?? payment.paymentId}
        isOpen={activeModal === 'void'}
        isSubmitting={voidMutation.isPending}
        errorMessage={voidErr}
        onSubmit={handleVoidSubmit}
        onCancel={() => setActiveModal(null)}
      />
    </PageShell>
  );
};

interface InfoBlockProps {
  label: string;
  value: string;
}

const InfoBlock = ({ label, value }: InfoBlockProps) => (
  <div className="mb-2 grid grid-cols-[110px_1fr] items-baseline gap-2 text-sm">
    <span className="text-[color:var(--content-muted)]">{label}</span>
    <span className="break-all text-[color:var(--content-strong)]">{value}</span>
  </div>
);

